import { Copy, LayoutGrid, List, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../app/paths';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../components/ui/menu';
import type { DiagramItem } from '../diagrams/diagrams-api';
import { useI18n } from '../i18n/i18n';
import { cn } from '../lib/cn';
import { DiagramThumbnail } from './diagram-thumbnail';
import type { DiagramPreview } from './thumbnail-layout';

/** Схема в списке пространства: метаданные и превью (docs/architecture.md §7.4). */
export type ListedDiagram = DiagramItem & { preview: DiagramPreview };
type View = 'table' | 'tiles';

const VIEW_KEY = 'acuta.home.view';

/** Вид списка — удобство браузера: хранилище может быть недоступно. */
function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'tiles' ? 'tiles' : 'table';
  } catch {
    return 'table';
  }
}

function writeView(view: View): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Приватный режим: выбор проживёт до перезагрузки.
  }
}

export interface DiagramActions {
  canCreate: boolean;
  canRename: boolean;
  canDelete: boolean;
  onCreate: () => void;
  onRename: (diagram: ListedDiagram) => void;
  onDuplicate: (diagram: ListedDiagram) => void;
  onDelete: (diagram: ListedDiagram) => void;
}

/** «Изменено»: «2 сент., 16:57», год — если не текущий (docs/saas/screens/блок-схемы.md). */
export function formatChanged(iso: string, locale: string, now: Date = new Date()): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Блок-схемы пространства свежеизменёнными сверху: таблица или плашки с мини-превью, «+» —
 * новая схема, «⋯» — переименовать, дублировать, удалить (по правам). Клик по строке и Enter
 * открывают схему.
 */
export function DiagramList({
  workspaceId,
  diagrams,
  loading = false,
  actions,
}: {
  workspaceId: string;
  diagrams: readonly ListedDiagram[];
  /** Первая страница ещё грузится — строка «Загрузка…» внутри списка. */
  loading?: boolean;
  actions: DiagramActions;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [view, setView] = useState<View>(readView);
  const open = (diagram: ListedDiagram) => void navigate(paths.diagram(workspaceId, diagram.id));
  const changeView = (next: View) => {
    setView(next);
    writeView(next);
  };
  const empty = !loading && diagrams.length === 0;

  return (
    <section className="mt-10" aria-label={t('home.diagrams')}>
      <header className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">{t('home.diagrams')}</h2>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label={t('home.view.label')}
            className="inline-flex items-center rounded-lg border border-border bg-card p-0.5"
          >
            <ViewButton
              active={view === 'table'}
              title={t('home.view.table')}
              onClick={() => changeView('table')}
            >
              <List className="size-4" />
            </ViewButton>
            <ViewButton
              active={view === 'tiles'}
              title={t('home.view.tiles')}
              onClick={() => changeView('tiles')}
            >
              <LayoutGrid className="size-4" />
            </ViewButton>
          </div>
          <button
            type="button"
            title={actions.canCreate ? t('home.newDiagram') : t('home.cannotCreate')}
            aria-label={t('home.newDiagram')}
            disabled={!actions.canCreate}
            onClick={actions.onCreate}
            className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors outline-none hover:bg-tint hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
          >
            <Plus className="size-4" />
          </button>
        </div>
      </header>

      {view === 'table' ? (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-sm">
            <thead className="border-b border-border">
              <tr>
                <th className={HEAD}>{t('home.column.name')}</th>
                <th className={cn(HEAD, 'w-44')}>{t('home.column.changed')}</th>
                <th className={cn(HEAD, 'w-12')}>
                  <span className="sr-only">{t('common.more')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {loading && (
                <tr className="border-b border-border">
                  <td colSpan={3} className="px-3 py-3 text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {empty && (
                <tr className="border-b border-border">
                  <td colSpan={3} className="px-3 py-8 text-center text-muted-foreground">
                    {t('home.empty')}
                  </td>
                </tr>
              )}
              {diagrams.map((diagram) => (
                <tr
                  key={diagram.id}
                  tabIndex={0}
                  onClick={() => open(diagram)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && event.target === event.currentTarget)
                      open(diagram);
                  }}
                  className="cursor-pointer border-b border-border transition-colors outline-none hover:bg-tint/50 focus-visible:bg-tint/50"
                >
                  <td className="max-w-0 px-3 py-3">
                    <p className="truncate font-medium" title={diagram.name}>
                      {diagram.name}
                    </p>
                    {diagram.description !== null && diagram.description !== '' && (
                      <p
                        className="truncate text-xs text-muted-foreground"
                        title={diagram.description}
                      >
                        {diagram.description}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">
                    {formatChanged(diagram.updatedAt, locale)}
                  </td>
                  <td
                    className="px-3 py-3 text-right"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <RowMenu diagram={diagram} actions={actions} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <p className="px-1 text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : empty ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t('home.empty')}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {diagrams.map((diagram) => (
            <li
              key={diagram.id}
              role="button"
              tabIndex={0}
              onClick={() => open(diagram)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && event.target === event.currentTarget) open(diagram);
              }}
              className="group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-card transition-shadow outline-none hover:shadow-[0_8px_24px_rgba(17,24,39,0.10)] focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {/* Превью абсолютом внутри бокса 4:3: иначе svg задаёт боксу высоту по viewBox. */}
              <div className="relative aspect-[4/3] border-b border-border bg-tint/40">
                <div className="absolute inset-3">
                  <DiagramThumbnail preview={diagram.preview} />
                </div>
              </div>
              <div className="flex items-start gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium" title={diagram.name}>
                    {diagram.name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {formatChanged(diagram.updatedAt, locale)}
                  </p>
                </div>
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <RowMenu
                    diagram={diagram}
                    actions={actions}
                    className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 data-[popup-open]:opacity-100"
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const HEAD =
  'h-10 px-3 text-left align-middle text-xs font-medium whitespace-nowrap text-muted-foreground';

function ViewButton({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40',
        active && 'bg-tint text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function RowMenu({
  diagram,
  actions,
  className,
}: {
  diagram: ListedDiagram;
  actions: DiagramActions;
  className?: string;
}) {
  const { t } = useI18n();
  if (!actions.canRename && !actions.canCreate && !actions.canDelete) return null;
  return (
    <Menu>
      <MenuTrigger>
        <button
          type="button"
          aria-label={t('home.actionsFor').replace('{name}', diagram.name)}
          className={cn(
            'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors outline-none hover:bg-tint hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40 data-[popup-open]:bg-tint',
            className,
          )}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </MenuTrigger>
      <MenuContent align="end">
        {actions.canRename && (
          <MenuItem onClick={() => actions.onRename(diagram)}>
            <Pencil />
            {t('diagram.rename')}
          </MenuItem>
        )}
        {actions.canCreate && (
          <MenuItem onClick={() => actions.onDuplicate(diagram)}>
            <Copy />
            {t('home.duplicate')}
          </MenuItem>
        )}
        {actions.canDelete && (
          <MenuItem danger onClick={() => actions.onDelete(diagram)}>
            <Trash2 />
            {t('common.delete')}
          </MenuItem>
        )}
      </MenuContent>
    </Menu>
  );
}
